import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * ChoicesEditor — the row-based choices editor for choice-based
 * Special Request response types (Dropdown Choice / Multiple
 * Choice), mirroring how the FormBuilder edits a choice field's
 * options: one choice per row, with add / remove / reorder. Values
 * are plain strings — the same shape stored in a record's
 * `dropdownOptions` and rendered to respondents.
 */
export default function ChoicesEditor({
  options,
  onChange,
  addLabel = 'Add choice',
}: {
  options: string[];
  onChange: (options: string[]) => void;
  addLabel?: string;
}) {
  const update = (idx: number, value: string) =>
    onChange(options.map((o, i) => (i === idx ? value : o)));
  const remove = (idx: number) => onChange(options.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-1.5">
      {options.length === 0 && (
        <p className="text-[11px] text-slate-400">No choices yet — add the options the organization can pick from.</p>
      )}
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            value={opt}
            onChange={(e) => update(i, e.target.value)}
            placeholder={`Choice ${i + 1}`}
            aria-label={`Choice ${i + 1}`}
            className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
          />
          <button
            type="button"
            onClick={() => move(i, -1)}
            disabled={i === 0}
            title="Move up"
            aria-label={`Move choice ${i + 1} up`}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30 cursor-pointer"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => move(i, 1)}
            disabled={i === options.length - 1}
            title="Move down"
            aria-label={`Move choice ${i + 1} down`}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30 cursor-pointer"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => remove(i)}
            title="Remove choice"
            aria-label={`Remove choice ${i + 1}`}
            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-500 cursor-pointer"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...options, ''])}
        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed border-slate-300 text-[11px] font-bold text-slate-500 hover:bg-slate-50 cursor-pointer"
      >
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}
